import { isNativePlatform, exportTablePdfNative, exportTableXlsxNative } from './capacitorExport';
import { formatMonetaryExport, foldToAscii, CSV_BOM } from './exportUtils';
import { withCurrencySymbolForCountry } from './currency';

// Re-exported so callers can import everything they need from one place
// (matches the report-export skill's documented contract). CSV_BOM must prefix
// every CSV a screen builds by hand so Excel decodes the preserved currency
// symbol (₹) as UTF-8.
export { formatMonetaryExport, foldToAscii, CSV_BOM };

export type ExportCell = string | number | null | undefined;
export type ExportFormat = 'csv' | 'excel' | 'pdf';

export interface ExportTableArgs {
  /** PDF heading + window title (UI label for the section). */
  title: string;
  /** Column headers — exact UI labels. */
  headers: string[];
  /** Row-by-column data. Cells are stringified + CSV-escaped safely. */
  rows: ExportCell[][];
  /** Base file name (extension optional — it is normalised per format). */
  filename: string;
  /** 'csv' or 'pdf'. */
  format: ExportFormat;
  /**
   * When true, the LAST row in `rows` is treated as a summary/total row and
   * rendered with emphasis in PDF (bold + tinted background + accent top
   * border). No effect on CSV — plain text has no styling.
   */
  totalRow?: boolean;
}

const cell = (v: ExportCell): string => (v === null || v === undefined ? '' : String(v));

function escapeHtml(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// ASCII-safe CSV escaping: fold to ASCII (currency symbols preserved), then wrap
// EVERY non-empty cell as literal text via the Excel/Sheets ="..." convention.
//
// A CSV carries no cell styling, so spreadsheet apps auto-detect numeric and
// date-looking cells ("42", "24/7/2026") and RIGHT-align them — which reads as
// jagged next to the left-aligned text cells in the same column. The PDF and XLSX
// exports pin every cell to the left (a deliberate, app-wide choice — see
// xlsxExport.ts); force-text is the only lever a CSV has, so applying it to all
// cells makes the CSV alignment match. The wrapper also inherently preserves
// leading zeros ("017869"), trailing decimal zeros ("12.50"), and long numeric IDs
// (Excel would otherwise show scientific notation), and neutralises formula
// injection (a leading =/+/-/@ can only ever evaluate to the quoted string).
// Exported so bespoke report CSV builders reuse the same rules.
export function escapeCsvCell(v: ExportCell): string {
  const str = foldToAscii(cell(v));
  if (str === '') return '""';
  // ="..." needs two escaping layers: first Excel-escape the quotes (double them
  // for the formula's string literal), then CSV-escape the whole field (double
  // them again). A single layer would turn a value containing a literal " (e.g.
  // an item name like 6" sub) into a malformed formula. Quote-free values —
  // nearly all cells — come out byte-identical to a plain quoted field's content.
  const field = `="${str.replace(/"/g, '""')}"`;
  return `"${field.replace(/"/g, '""')}"`;
}

// UTF-8 BOM: without it, Excel/SharePoint assume the system codepage (e.g.
// Windows-1252) instead of UTF-8, so any non-ASCII cell (e.g. the "—" placeholder
// for null SPLH) renders as mojibake ("â€"") instead of the intended character.
const UTF8_BOM = '\uFEFF';

function buildCsv(headers: string[], rows: ExportCell[][], metaRows?: ExportCell[][]): string {
  // BOM so Excel decodes the preserved currency symbol (\u20B9) as UTF-8; other
  // non-ASCII chars are still folded by escapeCsvCell.
  const metaLines = metaRows?.length ? [...metaRows.map((r) => r.map(escapeCsvCell).join(',')), ''] : [];
  return `${CSV_BOM}${[...metaLines, headers.map(escapeCsvCell).join(','), ...rows.map((r) => r.map(escapeCsvCell).join(','))].join('\r\n')}`;
}

function triggerDownloadWeb(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportedOnLabel(): string {
  return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const withExt = (filename: string, ext: string): string =>
  filename.toLowerCase().endsWith(`.${ext}`) ? filename : `${filename}.${ext}`;

// Web PDF = styled print window ("Print / Save as PDF"). Markup identical to the
// original Order Log / Items Overview exports so output stays consistent.
// `filename` (no extension) drives the popup's document.title — Chrome's "Save as
// PDF" print destination defaults the Save As name to document.title, so this is
// the only way to control the suggested filename on the web path (the `title`
// heading is a separate, human-facing string shown in the H1 + browser tab list).
function openPdfPrintWindow(title: string, filename: string, headers: string[], rows: ExportCell[][], totalRow = false, subtitle?: string, highlightRows?: Set<number>): void {
  const exportedOn = subtitle ?? `Exported on ${exportedOnLabel()}`;
  const thStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;text-align:left;font-size:11px;font-weight:600;color:#475569;background:#F5F6FA;text-transform:uppercase"';
  const tdStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;font-size:12px;color:#1A1D2E"';
  const tdTotalStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;font-size:12px;color:#1A1D2E;font-weight:700;background:#F5F6FA"';
  const ths = headers.map((h) => `<th ${thStyle}>${escapeHtml(h)}</th>`).join('');
  const lastIdx = rows.length - 1;
  // Single-outlet export: totalRow=true highlights only the last row.
  // Multi-outlet export: highlightRows carries every per-outlet total row's index instead.
  const isHighlighted = (i: number) => (highlightRows ? highlightRows.has(i) : totalRow && i === lastIdx);
  const trs = rows
    .map((r, i) => {
      const cellStyle = isHighlighted(i) ? tdTotalStyle : tdStyle;
      return `<tr>${r.map((c) => `<td ${cellStyle}>${escapeHtml(cell(c))}</td>`).join('')}</tr>`;
    })
    .join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(filename)}</title>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>body{font-family:'DM Sans',sans-serif;padding:40px;color:#1A1D2E}h1{font-size:18px;font-weight:700;margin-bottom:4px}.sub{font-size:12px;color:#475569;margin-bottom:20px}table{width:100%;border-collapse:collapse;margin-top:8px}.brand{display:flex;align-items:center;gap:10px;margin-bottom:24px}.logo{width:28px;height:28px;border-radius:8px;background:linear-gradient(135deg,#D23A3B,#E86B6B);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px}.bname{font-size:14px;font-weight:700}.bsub{font-size:9px;color:#64748B;letter-spacing:0.5px}@media print{body{padding:20px}button{display:none!important}}</style>
  </head><body>
    <div class="brand"><div class="logo">M</div><div><div class="bname">Maghil</div><div class="bsub">ANALYTICS</div></div></div>
    <h1>${escapeHtml(title)}</h1>
    <div class="sub">${escapeHtml(exportedOn)}</div>
    <button onclick="window.print()" style="padding:8px 20px;background:#D23A3B;color:#fff;border:none;border-radius:6px;font-family:'DM Sans';font-size:12px;font-weight:600;cursor:pointer;margin-bottom:16px">Print / Save as PDF</button>
    <table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>
  </body></html>`;
  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

/**
 * Generic report-table export engine. Handles CSV + PDF on web AND Capacitor
 * native (no per-call branching by the caller). The ONLY entry point screens
 * should use for new table exports (directly or via ReportSectionExportButton).
 */
export async function exportTable({ title, headers, rows, filename, format, totalRow = false }: ExportTableArgs): Promise<void> {
  if (format === 'csv') {
    // Native saves a real .xlsx instead of CSV: the Google Sheets Android app
    // decodes CSV as Latin-1, mangling the currency symbol (₹ → "â‚¹") and the
    // BOM (→ "ï»¿"); .xlsx carries its own encoding so the exact symbol renders
    // in Excel AND Sheets.
    if (isNativePlatform()) {
      await exportTableXlsxNative(headers, rows, withExt(filename, 'xlsx'), title);
      return;
    }
    const csv = buildCsv(headers, rows);
    triggerDownloadWeb(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), withExt(filename, 'csv'));
    return;
  }

  if (format === 'excel') {
    if (isNativePlatform()) {
      await exportTableXlsxNative(headers, rows, withExt(filename, 'xlsx'), title);
      return;
    }
    // Dynamically imported so fflate stays out of the initial web bundle too.
    const { buildXlsx, xlsxFilename } = await import('./xlsxExport');
    const stringRows = rows.map((r) => r.map(cell));
    const bytes = buildXlsx(headers, stringRows, title);
    triggerDownloadWeb(
      new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      xlsxFilename(filename),
    );
    return;
  }

  // PDF
  const name = withExt(filename, 'pdf');
  if (isNativePlatform()) {
    const stringRows = rows.map((r) => r.map(cell));
    await exportTablePdfNative(title, `Exported on ${exportedOnLabel()}`, headers, stringRows, name, totalRow);
    return;
  }
  openPdfPrintWindow(title, filename, headers, rows, totalRow);
}

export interface OutletForExport {
  id: string;
  name: string;
  /** ISO country code (e.g. 'IN', 'US') — drives the per-outlet currency symbol. */
  country?: string;
}

interface PerOutletResult {
  headers: string[];
  rows: ExportCell[][];
  totalRow?: boolean;
}

export interface MultiOutletExportArgs {
  /** PDF heading / Excel title row / window title (same as ExportTableArgs.title). */
  title: string;
  filename: string;
  format: ExportFormat;
  outlets: OutletForExport[];
  brandName: string;
  dateRangeLabel: string;
  /** Fetch this outlet's data — same shape a single-outlet buildExport returns. */
  fetchForOutlet: (outlet: OutletForExport) => Promise<PerOutletResult>;
  /** Called after each outlet finishes, for a "Fetching N / M outlets…" indicator. */
  onProgress?: (done: number, total: number) => void;
}

/**
 * "All outlets" export: fetches every accessible outlet's data, merges into one
 * table with a leading Outlet column sorted by outlet name, and stamps a
 * brand/outlets/date-range/generated-at header — CSV/Excel as leading rows,
 * PDF as the subtitle line.
 *
 * Fetches run SEQUENTIALLY (not in parallel) — each outlet's fetch is wrapped in
 * `withCurrencySymbolForCountry` so every existing `getCurrencySymbol()` /
 * `fmtCurrency()` / `moneyHeader()` call inside `fetchForOutlet` (== a section's
 * `buildExport`) resolves to THAT outlet's currency instead of the globally-
 * selected outlet's, with no changes needed at any of the ~50 buildExport call
 * sites. That override is a single module-level variable, so it is only safe
 * when calls don't overlap — do not reintroduce concurrency here without also
 * reworking the symbol override to be non-global.
 */
export async function exportTableMultiOutlet({
  title, filename, format, outlets, brandName, dateRangeLabel, fetchForOutlet, onProgress,
}: MultiOutletExportArgs): Promise<void> {
  const sorted = [...outlets].sort((a, b) => a.name.localeCompare(b.name));
  const results = new Array<PerOutletResult | null>(sorted.length).fill(null);

  for (let idx = 0; idx < sorted.length; idx++) {
    try {
      results[idx] = await withCurrencySymbolForCountry(sorted[idx].country, () => fetchForOutlet(sorted[idx]));
    } catch (err) {
      // Don't let one outlet's failure silently blank its rows out of the merged
      // export with no trace — surface it in the console so a partial "All outlets"
      // export is diagnosable instead of looking like "only my outlet has data".
      console.error(`[exportTableMultiOutlet] fetch failed for outlet "${sorted[idx].name}" (${sorted[idx].id}):`, err);
      results[idx] = { headers: [], rows: [] };
    }
    onProgress?.(idx + 1, sorted.length);
  }

  const baseHeaders = results.find((r) => r && r.headers.length)?.headers ?? [];
  const headers = ['Outlet', ...baseHeaders];
  const rows: ExportCell[][] = [];
  // Row indices that are a per-outlet total/summary row — carried into the PDF (and
  // Excel) renderers so each outlet's total gets the same bold+shaded emphasis a
  // single-outlet export's total row gets, instead of blending in as a plain data row.
  const highlightRows = new Set<number>();
  sorted.forEach((outlet, i) => {
    const outletResult = results[i];
    const outletRows = outletResult?.rows ?? [];
    if (outletRows.length === 0) {
      // Zero matching records for this outlet is a real, reportable result — a
      // placeholder row keeps the outlet visible in the merged file instead of
      // silently vanishing (which reads as "skipped" rather than "checked, empty").
      rows.push([outlet.name, ...baseHeaders.map(() => '-')]);
    } else {
      outletRows.forEach((row, rowIdx) => {
        rows.push([outlet.name, ...row]);
        if (outletResult?.totalRow && rowIdx === outletRows.length - 1) {
          highlightRows.add(rows.length - 1);
        }
      });
    }
  });

  const generatedAt = new Date().toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  const metaRows: ExportCell[][] = [
    ['Brand', brandName],
    ['Outlets', sorted.map((o) => o.name).join(', ')],
    ['Date range', dateRangeLabel],
    ['Generated', generatedAt],
  ];
  const subtitle = `${brandName} • ${sorted.length} outlets • ${dateRangeLabel} • Generated ${generatedAt}`;

  if (format === 'pdf') {
    const name = withExt(filename, 'pdf');
    if (isNativePlatform()) {
      const stringRows = rows.map((r) => r.map(cell));
      await exportTablePdfNative(title, subtitle, headers, stringRows, name, false, highlightRows);
      return;
    }
    openPdfPrintWindow(title, filename, headers, rows, false, subtitle, highlightRows);
    return;
  }

  if (format === 'excel') {
    if (isNativePlatform()) {
      await exportTableXlsxNative(headers, rows, withExt(filename, 'xlsx'), title, metaRows, highlightRows);
      return;
    }
    const { buildXlsx, xlsxFilename } = await import('./xlsxExport');
    const stringRows = rows.map((r) => r.map(cell));
    const bytes = buildXlsx(headers, stringRows, title, metaRows, highlightRows);
    triggerDownloadWeb(
      new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      xlsxFilename(filename),
    );
    return;
  }

  // CSV — same native→xlsx shim as the single-outlet path (Google Sheets Android
  // decodes CSV as Latin-1, mangling the currency symbol and BOM).
  if (isNativePlatform()) {
    await exportTableXlsxNative(headers, rows, withExt(filename, 'xlsx'), title, metaRows);
    return;
  }
  const csv = buildCsv(headers, rows, metaRows);
  triggerDownloadWeb(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), withExt(filename, 'csv'));
}
